import { Button } from "@mui/material";
import {
  checkIfUnauthorized,
  getPersonalClientName,
  getRandomNumber,
} from "common/functions";
import ClientPortfolioTemplate from "components/clientPortfolioTemplate/ClientPortfolioTemplate";
import MainTemplate from "components/mainTemplate/MainTemplate";
import {
  PersonalClientAccountHistoryRoute,
  UnauthorizedRoute,
} from "components/paths";
import { openSnackbar } from "components/snackbar";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useActions, useReduxState } from "store";
import styled from "styled-components";
import {
  Account,
  CreditCard,
  GeneralLedger,
  GIC,
  LineOfCredit,
  Loan,
  Mortgage,
  MutualFunds,
  PersonalChequing,
  PersonalClient,
  PersonalSavings,
} from "types";
import { v4 } from "uuid";
import AddAccountMenu from "./components/AddAccountMenu";
import CreditCardDisplay from "./components/CreditCardDisplay";
import EditAccountDialog from "./components/EditAccountDialog";
import GICDisplay from "./components/GICDisplay";
import LineOfCreditDisplay from "./components/LineOfCreditDisplay";
import LoanDisplay from "./components/LoanDisplay";
import MortgageDisplay from "./components/MortgageDisplay";
import MutualFundsDisplay from "./components/MutualFundsDisplay";
import PersonalChequingDisplay from "./components/PersonalChequingDisplay";
import PersonalSavingsDisplay from "./components/PersonalSavingsDisplay";
import { getPersonalClientById, getGeneralLedger } from "pages/lessons/helpers";

export type Props = {
  clientId: string;
  lessonId: string;
  studentId?: string;
};

export default function PersonalClientAccounts(props: Props) {
  const {
    getLessonNameAndData,
    getDataforViewAsStudent,
    lessonId,
  } = useReduxState((e) => e.lesson);
  const lessonActions = useActions().lesson;
  const clientInfoActions = useActions().personalClientInformation;
  const [state, setState] = useState<PersonalClient>(
    getPersonalClientById(
      props.clientId,
      props.studentId
        ? getDataforViewAsStudent.data?.lessonData
        : getLessonNameAndData.data?.lessonData
    )
  );
  const [glState, setGLState] = useState<GeneralLedger>(
    getGeneralLedger(
      props.studentId
        ? getDataforViewAsStudent.data?.lessonData
        : getLessonNameAndData.data?.lessonData
    )
  );
  const [accountInEdit, setAccountInEdit] = useState<Account | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const history = useHistory();
  if (checkIfUnauthorized(getLessonNameAndData, props.studentId)) {
    history.push(UnauthorizedRoute);
  }
  useEffect(() => {
    if (props.studentId === undefined) {
      setIsLoading(true);
      if (!getLessonNameAndData.data?.lessonData) {
        lessonActions.getLessonNameAndData({
          id: (props.lessonId as unknown) as number,
          onComplete: (payload) => {
            if (props.clientId) {
              let client: PersonalClient = getPersonalClientById(
                props.clientId,
                payload.lessonData
              );

              if (!!client.identification?.clientId) {
                setState(client);
                setGLState(getGeneralLedger(payload.lessonData));
              } else history.push(UnauthorizedRoute);
            }
            setIsLoading(false);
          },
        });
      } else {
        if (props.clientId) {
          setState(
            getPersonalClientById(
              props.clientId,
              getLessonNameAndData.data.lessonData
            )
          );
          setGLState(getGeneralLedger(getLessonNameAndData.data.lessonData));
          setIsLoading(false);
        }
      }
    }
    if (props.studentId) {
      setIsLoading(true);
      if (!getDataforViewAsStudent.data?.lessonData) {
        lessonActions.getDataforViewAsStudent({
          id: (props.lessonId as unknown) as number,
          studentId: props.studentId,
          onComplete: (payload) => {
            if (props.clientId) {
              setState(
                getPersonalClientById(props.clientId, payload.lessonData)
              );
              setGLState(getGeneralLedger(payload.lessonData));
            }
            setIsLoading(false);
          },
        });
      } else {
        if (props.clientId) {
          setState(
            getPersonalClientById(
              props.clientId,
              getDataforViewAsStudent.data.lessonData
            )
          );
          setGLState(getGeneralLedger(getDataforViewAsStudent.data.lessonData));
          setIsLoading(false);
        }
      }
    }
  }, []);

  const clientName = getPersonalClientName(state);

  function getupdatedClient(account: Account) {
    const isNew = state.accounts
      ? !state.accounts.some((a) => a.accountId === account.accountId)
      : "true";
    const updatedClient: PersonalClient = {
      ...state,
      accounts: state.accounts
        ? isNew
          ? [...state.accounts, account]
          : state.accounts.map((a) =>
              account.accountId === a.accountId ? { ...account } : a
            )
        : [account],
    };
    return updatedClient;
  }

  function onDeleteClick(accountId: string) {
    const accounts = state.accounts || [];
    const updatedClient = {
      ...state,
      accounts: accounts.filter((a) => a.accountId !== accountId),
    };
    //setState(updatedClient);
    setAccountInEdit(undefined);
    onSave(updatedClient);
  }

  // PersonalSavings starts with 2
  function addPersonalSavings() {
    const newAccount: PersonalSavings = {
      accountId: v4(),
      accountType: "PERSONAL_SAVINGS",
      accountNumber: 2 + getRandomNumber(6), // as per business req personal savings should start with 2
      balance: 0.0,
      created: moment().format(),
    };
    setAccountInEdit(newAccount);
  }

  // PersonalChequing starts with 1
  function addPersonalChequing() {
    const newAccount: PersonalChequing = {
      accountId: v4(),
      accountType: "PERSONAL_CHEQUING",
      accountNumber: 1 + getRandomNumber(6), // as per business req personal chequing should start with 1
      balance: 0.0,
      created: moment().format(),
    };
    setAccountInEdit(newAccount);
  }

  function addCreditCard() {
    const newAccount: CreditCard = {
      accountId: v4(),
      accountType: "CREDIT_CARD",
      cardNumber: 8520 + getRandomNumber(12), // as per business req credit card number should be randomized and start with 8520
      balance: 0.0,
      created: moment().format(),
    };
    setAccountInEdit(newAccount);
  }

  function addGIC() {
    const newAccount: GIC = {
      accountId: v4(),
      accountType: "GIC",
      accountNumber: getRandomNumber(8),
      created: moment().format(),
      investmentCategory: "Cashable",
    };
    setAccountInEdit(newAccount);
  }

  function addMutualFunds() {
    const newAccount: MutualFunds = {
      accountId: v4(),
      accountType: "MUTUAL_FUNDS",
      accountNumber: getRandomNumber(9),
      created: moment().format(),
    };
    setAccountInEdit(newAccount);
  }

  function addLoan() {
    const newAccount: Loan = {
      accountId: v4(),
      accountType: "LOAN",
      accountNumber: getRandomNumber(10),
      balance: 0.0,
      created: moment().format(),
      interestType: "Fixed",
      type: "Secured",
    };
    setAccountInEdit(newAccount);
  }

  //Personal LOC should start with 3
  function addLineOfCredit() {
    const newAccount: LineOfCredit = {
      accountId: v4(),
      accountType: "LINE_OF_CREDIT",
      accountNumber: 3 + getRandomNumber(6), // as per business req LOC should start with 3
      balance: 0.0,
      created: moment().format(),
      interestType: "Variable",
    };
    setAccountInEdit(newAccount);
  }

  function addMortgage() {
    const newAccount: Mortgage = {
      accountId: v4(),
      accountType: "MORTGAGE",
      accountNumber: getRandomNumber(6),
      balance: 0.0,
      created: moment().format(),
      interestType: "Fixed",
      type: "Open",
    };
    setAccountInEdit(newAccount);
  }

  function onSave(updatedClient: PersonalClient) {
    setIsLoading(true);
    clientInfoActions.savePersonalClient({
      client: updatedClient,
      lessonId,
      onComplete: (payload) => {
        if (!!payload.clientId) {
          openSnackbar({
            severity: "success",
            message: `Personal Client '${clientName}' saved successfully`,
          });
          setState(updatedClient);
          lessonActions.savePersonalClient(updatedClient);
        } else {
          openSnackbar({
            severity: "error",
            message: `Error saving personal client '${clientName}'. Please refresh the page and try again!`,
          });
        }
        setIsLoading(false);
        setAccountInEdit(undefined);
      },
    });
  }

  function onEditClick(account: Account) {
    setAccountInEdit(account);
  }

  function onAccountHistoryClick(accountId: string) {
    !!props.studentId
      ? history.push(
          `/${lessonId}${PersonalClientAccountHistoryRoute}/${state.identification?.clientId}/${accountId}/${props.studentId}`
        )
      : history.push(
          `/${lessonId}${PersonalClientAccountHistoryRoute}/${state.identification?.clientId}/${accountId}`
        );
  }
  const isReadOnlyStudentAccess =
    getLessonNameAndData.data?.isReadOnly ?? false;

  return (
    <MainTemplate
      title={
        isLoading ? "Client Portfolio" : `Client Portfolio — ${clientName}`
      }
      isLoading={isLoading}
      studentId={props.studentId}
      cashDrawerBalance={glState.cashDrawer?.balance ?? 0}
    >
      <ClientPortfolioTemplate selectedButton={2} studentId={props.studentId}>
        <Content>
          {!isLoading && (
            <>
              <FlexDiv>
                <ListingHeading>Open</ListingHeading>
                <AddAccountMenu
                  onAddPersonalChequing={() => {
                    addPersonalChequing();
                  }}
                  onAddPersonalSavings={() => {
                    addPersonalSavings();
                  }}
                  onAddGIC={() => {
                    addGIC();
                  }}
                  onAddMutualFunds={() => {
                    addMutualFunds();
                  }}
                  onAddCreditCard={() => {
                    addCreditCard();
                  }}
                  onAddLoan={() => {
                    addLoan();
                  }}
                  onAddLineOfCredit={() => {
                    addLineOfCredit();
                  }}
                  onAddMortgage={() => {
                    addMortgage();
                  }}
                  isReadOnlyView={!!props.studentId || isReadOnlyStudentAccess}
                />
              </FlexDiv>
              <AccountList
                accounts={state.accounts}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onAccountHistoryClick={onAccountHistoryClick}
                show={"open"}
                isReadOnlyView={!!props.studentId || isReadOnlyStudentAccess}
              />
              <ClosedAccountSection>
                <ListingHeading>Closed</ListingHeading>
                <AccountList
                  accounts={state.accounts}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick}
                  onAccountHistoryClick={onAccountHistoryClick}
                  show={"close"}
                  isReadOnlyView={!!props.studentId || isReadOnlyStudentAccess}
                />
              </ClosedAccountSection>
            </>
          )}
        </Content>
      </ClientPortfolioTemplate>
      {!!accountInEdit && (
        <EditAccountDialog
          account={accountInEdit}
          isSaving={isLoading}
          onSave={(account) => {
            const updatedClient = getupdatedClient(account);
            onSave(updatedClient);
          }}
          onCancel={() => {
            setAccountInEdit(undefined);
          }}
        />
      )}
    </MainTemplate>
  );
}

function accountSorter(a: Account, b: Account): number {
  //Account Sort Order
  // - Personal Chequing
  // - Personal Savings
  // - GIC
  // - Mutual Fund
  // - Credit Card
  // - Loan
  // - Line of Credit
  // - Mortgage

  const sortOrder = [
    "PERSONAL_CHEQUING",
    "PERSONAL_SAVINGS",
    "GIC",
    "MUTUAL_FUNDS",
    "CREDIT_CARD",
    "LOAN",
    "LINE_OF_CREDIT",
    "MORTGAGE",
  ];

  if (a.accountType === b.accountType) {
    if (!!a.openDate && !!b.openDate) {
      return b.created.localeCompare(a.created);
    }
    if (!a.openDate && !b.openDate) {
      return b.created.localeCompare(a.created);
    }
    if (!a.openDate) {
      return 1;
    }
    return -1;
  }

  const aOrder = sortOrder.indexOf(a.accountType);
  const bOrder = sortOrder.indexOf(b.accountType);
  return aOrder - bOrder;
}

type AccountListProps = {
  accounts?: Account[];
  onEditClick(account: Account): void;
  onDeleteClick(accountId: string): void;
  onAccountHistoryClick(accountId: string): void;
  isReadOnlyView: boolean;
  show: "open" | "close";
};

function AccountList({
  accounts,
  onEditClick,
  onDeleteClick,
  onAccountHistoryClick,
  isReadOnlyView,
  show,
}: AccountListProps) {
  const [displayClosedAcc, setDisplayClosedAcc] = useState<boolean>();

  const list = accounts
    ? accounts
        .filter(
          (a) =>
            (show === "close" &&
              !!a.closeDate &&
              a.closeDate !== "Invalid date") ||
            (show === "open" &&
              (!a.closeDate || a.closeDate === "Invalid date"))
        )
        .sort(accountSorter)
    : [];

  if (list.length === 0) {
    return (
      <FlexDiv>
        <ListMessage>
          There are no {show === "open" ? "open" : "closed"} accounts yet.
        </ListMessage>
      </FlexDiv>
    );
  }

  return (
    <>
      {show === "close" && (
        <FlexDiv>
          <ListMessage>
            {list.length === 1
              ? `There is 1 closed account.`
              : `There are ${list.length} closed accounts.`}
          </ListMessage>

          <Button
            onClick={() => {
              setDisplayClosedAcc(!displayClosedAcc);
            }}
          >
            {displayClosedAcc ? "Hide" : "View"}
          </Button>
        </FlexDiv>
      )}
      {(show === "open" || (show === "close" && displayClosedAcc)) &&
        list.map((account, index) => {
          if (account.accountType === "PERSONAL_SAVINGS") {
            return (
              <PersonalSavingsDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "PERSONAL_CHEQUING") {
            return (
              <PersonalChequingDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "GIC") {
            return (
              <GICDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "MUTUAL_FUNDS") {
            return (
              <MutualFundsDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "CREDIT_CARD") {
            return (
              <CreditCardDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "LOAN") {
            return (
              <LoanDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "LINE_OF_CREDIT") {
            return (
              <LineOfCreditDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          if (account.accountType === "MORTGAGE") {
            return (
              <MortgageDisplay
                key={account.accountId}
                onEditClick={() => onEditClick(account)}
                onDeleteClick={() => {
                  onDeleteClick(account.accountId);
                }}
                onAccountHistoryClick={() =>
                  onAccountHistoryClick(account.accountId)
                }
                account={account}
                isReadOnlyView={isReadOnlyView}
              />
            );
          }
          return <div key={index}>unknown account</div>;
        })}
    </>
  );
}

const ListingHeading = styled.div`
  margin: 16px 0;
  font-size: 20px;
  color: #333;
  flex: 1;
`;

const FlexDiv = styled.div`
  display: flex;
  width: 730px;
  margin-bottom: 5px;
  align-items: center;
`;

const ListMessage = styled.div`
  color: #777;
  padding-bottom: 5px;
  margin-right: 10px;
`;

const Content = styled.div`
  margin: 2em auto;
  width: max-content;
`;

const ClosedAccountSection = styled.div`
  margin-top: 2em;
`;
